import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import CustomInputLabel from '../common/CustomInputLabel';
import useDebouncedState from '../../hooks/useDebouncedState';
import CreateReferrerCompanyButton from './CreateReferrerCompanyButton';
import { useGetReferrerCompaniesQuery } from '../../redux/services/referrerCompanyApi';

const getOptions = companies => (
  (companies || []).map(c => ({
    label: c.name,
    value: c.id,
  }))
);

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({text: value, limit});
};

const FindReferrerCompanyInput = props => {
  const {onSubmit, limit} = props;
  const [textInput, setTextInput] = useState('');
  const [selectedReferrerCompanyId, setSelectedReferrerCompanyId] = useState(props.defaultValue);
  const [query, setQuery] = useState({limit});
  const [debouncedQuery, setDebouncedQuery] = useState({limit});
  useDebouncedState(query, setDebouncedQuery, 500);
  const selectedReferrerCompanyQuery = useGetReferrerCompaniesQuery(
    {id: selectedReferrerCompanyId},
    {skip: !selectedReferrerCompanyId}
  );
  const selectedReferrerCompany = selectedReferrerCompanyQuery?.data?.data?.[0];
  const referrerCompaniesQuery = useGetReferrerCompaniesQuery(
    {...debouncedQuery},
    {skip: !get(debouncedQuery, 'text')}
  );
  const isLoading = referrerCompaniesQuery.isLoading || selectedReferrerCompanyQuery.isLoading;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  const referrers = (get(referrerCompaniesQuery, 'data.data') || []);
  const options = textInput ? getOptions(referrers) : [];
  if (selectedReferrerCompanyId) {
    return (
      <Box sx={{minWidth: props.minWidth, minHeight: props.minHeight}}>
        <Stack 
          alignItems={props?.align || 'flex-start'}
          padding={props.padding}
        >
          {props.label && <CustomInputLabel>{props.label}</CustomInputLabel>}
          <Chip
            color='default'
            variant={props?.variant || undefined}
            label={isLoading ? 'Loading...' : selectedReferrerCompany?.name}
            onDelete={props.disabled ? undefined : () => {
              setTextInput('');
              setSelectedReferrerCompanyId(null);
              onSubmit({target: {value: null}}, null);
              setQuery({});
            }}
          >
          </Chip>
        </Stack>
      </Box>
    );
  }
  return (
    <Stack style={{width: props.width || props.fullWidth ? '100%' : null}}>
      {props.label && <CustomInputLabel>{props.label}</CustomInputLabel>}
      <Autocomplete
        type='text'
        style={{
          minWidth: props.minWidth || null, 
          maxWidth: props.maxWidth || null
        }}
        options={options}
        filterOptions={x => x}
        loading={isLoading}
        disabled={!!props.disabled}
        onChange={(e,val,reason) => {
          if (reason === 'selectOption') {
            setSelectedReferrerCompanyId(val?.id || val?.value || null);
            if (val.id) return onSubmit(e, val.id);
            if (val.value) return onSubmit(e, val.value);
          }
          debouncedTextChangeHandler(e);
        }}
        renderInput={params => (
          <TextField
            placeholder={props.placeholder}
            onChange={debouncedTextChangeHandler}
            {...params}
            sx={props.sx}
            size={props.size}
            fullWidth={props.fullWidth}
            className={`${props.rounded && 'custom-text-field-rounded'}`}
            minWidth={props.minWidth}
            InputProps={{
              ...params.InputProps,
              autoFocus: props.autoFocus,
              startAdornment: <InputAdornment position="start">
                <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
              </InputAdornment>,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        {...pick(
          props,
          'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
          'size',
          'fullWidth',
          'minWidth'
        )}
        />
        {props.allowCreateNew && 
          <CreateReferrerCompanyButton 
            afterSubmit={(e, id) => {
              setSelectedReferrerCompanyId(id);
              onSubmit(e, id)
            }}
          />
        }
    </Stack>
  );

};

FindReferrerCompanyInput.defaultProps = {
  autoFocus: false,
  defaultValue: null,
  disableClearable: false,
  freeSolo: true,
  id: 'FindReferrerCompanyInput.Autocomplete',
  placeholder: 'Search Lead Companies',
  onSubmit: ({referrerCompanyId}) => console.log('FindReferrerCompanyInput.onSubmit', referrerCompanyId),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
};

FindReferrerCompanyInput.propTypes = {
  autoFocus: PropTypes.bool,
  defaultValue: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
};

export default FindReferrerCompanyInput;